import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { toIsoDate, formatDate } from "../lib/dates";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import QuoteFlow from "../components/QuoteFlow";

const GOOGLE_MAPS_KEY = import.meta.env.VITE_GOOGLE_MAPS_KEY || "";

// Haversine distance in miles
function distanceMiles(lat1, lng1, lat2, lng2) {
  const R = 3958.8;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat/2)**2 + Math.cos(lat1*Math.PI/180)*Math.cos(lat2*Math.PI/180)*Math.sin(dLng/2)**2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
}

async function geocodeAddress(address) {
  if (!GOOGLE_MAPS_KEY || !address) return null;
  try {
    const r = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${GOOGLE_MAPS_KEY}`);
    const d = await r.json();
    if (d.results?.[0]) {
      const { lat, lng } = d.results[0].geometry.location;
      return { lat, lng };
    }
  } catch(e) { console.warn("Geocode failed:", e); }
  return null;
}
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);

const VEHICLES = [
  { id:"sedan", label:"Sedan / Coupe",  icon:"🚗" },
  { id:"suv",   label:"SUV / Crossover",icon:"🚙" },
  { id:"truck", label:"Truck",          icon:"🛻" },
  { id:"van",   label:"Van / Minivan",  icon:"🚐" },
];
const WEEK_DAYS    = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
const DAY_KEYS     = ["sun","mon","tue","wed","thu","fri","sat"];

function getTimeSlots(start = 8, end = 17) {
  const slots = [];
  for (let h = start; h < end; h++) {
    const suffix = h < 12 ? "AM" : "PM";
    const disp   = h === 0 ? 12 : h <= 12 ? h : h - 12;
    slots.push(`${disp}:00 ${suffix}`);
  }
  return slots;
}
const MONTH_NAMES = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const fmt$ = n => `$${n}`;

// Calendar helpers
const today = new Date();
today.setHours(0,0,0,0);
function getCalendarData(year, month) {
  const firstDay = new Date(year, month, 1).getDay(); // 0=Sun
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  return { firstDay, daysInMonth };
}

const css = `
  .bp-shell { min-height:100vh; }
  .bp-nav { display:flex; align-items:center; justify-content:center; gap:12px; padding:14px 28px; background:var(--surface); border-bottom:1px solid var(--border); position:sticky; top:0; z-index:100; }
  .bp-logo { font-family:var(--head); font-size:22px; font-weight:800; letter-spacing:2px; }
  .bp-logo span { color:var(--blue); }

  .booking-page { max-width:860px; margin:0 auto; padding:40px 20px; }
  .booking-hero { text-align:center; margin-bottom:44px; }
  .booking-hero h1 { font-family:var(--head); font-size:clamp(38px,7vw,72px); font-weight:800; letter-spacing:3px; line-height:1.05; }
  .booking-hero h1 span { color:var(--blue); }
  .booking-hero p { color:var(--muted); margin-top:10px; font-size:16px; }
  .booking-hero .biz-meta { display:flex; align-items:center; justify-content:center; gap:16px; margin-top:14px; flex-wrap:wrap; }
  .booking-hero .biz-meta span { font-size:13px; color:var(--muted); display:flex; align-items:center; gap:5px; }

  .step-indicator { display:flex; align-items:center; justify-content:center; margin-bottom:36px; }
  .step-dot { width:34px; height:34px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-weight:600; font-size:13px; border:2px solid var(--border); background:var(--card); color:var(--muted); transition:all .3s; }
  .step-dot.active { border-color:var(--blue); background:var(--blue); color:#fff; }
  .step-dot.done   { border-color:var(--teal); background:var(--teal); color:#fff; }
  .step-line { width:56px; height:2px; background:var(--border); transition:all .3s; }
  .step-line.done { background:var(--teal); }

  .section-label { font-family:var(--head); font-size:13px; font-weight:700; letter-spacing:3px; color:var(--blue); text-transform:uppercase; margin-bottom:14px; }
  .services-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(240px,1fr)); gap:12px; margin-bottom:28px; }
  .service-card { background:var(--card); border:2px solid var(--border); border-radius:14px; padding:18px; cursor:pointer; transition:all .25s; position:relative; }
  .service-card:hover { border-color:var(--blue); transform:translateY(-2px); }
  .service-card.selected { border-color:var(--blue); background:var(--card2); box-shadow:0 0 22px var(--blue-glow); }
  .service-card .s-icon { font-size:26px; margin-bottom:8px; }
  .service-card h3 { font-family:var(--head); font-size:18px; font-weight:700; letter-spacing:1px; margin-bottom:4px; }
  .service-card p  { color:var(--muted); font-size:13px; line-height:1.5; margin-bottom:10px; }
  .service-card .price { font-family:var(--head); font-size:20px; font-weight:700; color:var(--teal); }
  .service-card .dur   { font-size:12px; color:var(--muted); margin-top:2px; }
  .service-card .check { position:absolute; top:12px; right:12px; width:20px; height:20px; border-radius:50%; background:var(--blue); display:flex; align-items:center; justify-content:center; font-size:11px; opacity:0; transition:.2s; }
  .service-card.selected .check { opacity:1; }

  .vehicle-grid { display:grid; grid-template-columns:repeat(2,1fr); gap:10px; margin-bottom:28px; }
  @media(min-width:480px){ .vehicle-grid { grid-template-columns:repeat(4,1fr); } }
  .vehicle-card { background:var(--card); border:2px solid var(--border); border-radius:12px; padding:16px 10px; text-align:center; cursor:pointer; transition:all .2s; }
  .vehicle-card:hover { border-color:var(--blue); }
  .vehicle-card.selected { border-color:var(--blue); background:var(--card2); }
  .vehicle-card .vicon { font-size:30px; margin-bottom:6px; }
  .vehicle-card span { font-size:12px; color:var(--muted); font-weight:500; }

  .date-grid { display:grid; grid-template-columns:repeat(7,1fr); gap:5px; margin-bottom:8px; }
  .day-label { text-align:center; font-size:11px; font-weight:600; color:var(--muted); padding:4px 0; }
  .date-btn  { aspect-ratio:1; border:none; border-radius:8px; background:var(--card); color:var(--text); font-family:var(--body); font-size:13px; cursor:pointer; transition:all .2s; border:2px solid transparent; }
  .date-btn:hover { border-color:var(--blue); }
  .date-btn.selected { background:var(--blue); color:#fff; }
  .date-btn.empty    { background:transparent; cursor:default; border:none; }
  .date-btn.today    { border-color:var(--teal); }

  .time-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:8px; margin-bottom:28px; }
  .time-btn  { padding:11px; border:2px solid var(--border); border-radius:10px; background:var(--card); color:var(--text); font-family:var(--body); font-size:13px; cursor:pointer; transition:all .2s; }
  .time-btn:hover   { border-color:var(--blue); }
  .time-btn.selected { border-color:var(--blue); background:var(--card2); color:var(--blue); }
  .time-btn.booked   { opacity:.3; cursor:not-allowed; text-decoration:line-through; }

  .form-grid { display:grid; grid-template-columns:1fr 1fr; gap:14px; margin-bottom:28px; }
  @media(max-width:480px){ .form-grid { grid-template-columns:1fr; } }
  .form-full { grid-column:1/-1; }

  .summary-box { background:var(--card2); border:1px solid var(--border); border-radius:14px; padding:18px; margin-bottom:20px; }
  .summary-row { display:flex; justify-content:space-between; align-items:center; padding:7px 0; border-bottom:1px solid var(--border); font-size:14px; }
  .summary-row:last-child { border-bottom:none; font-weight:600; font-size:15px; color:var(--teal); }
  .summary-label { color:var(--muted); }

  .btn-row { display:flex; gap:10px; margin-top:8px; }
  .btn-row .btn-primary, .btn-row .btn-secondary { flex:1; justify-content:center; }

  .success-screen { text-align:center; padding:60px 20px; }
  .success-icon { font-size:68px; margin-bottom:20px; animation:pop .4s cubic-bezier(.175,.885,.32,1.275); }
  .success-screen h2 { font-family:var(--head); font-size:38px; font-weight:800; letter-spacing:2px; margin-bottom:12px; }
  .success-screen p  { color:var(--muted); font-size:15px; max-width:380px; margin:0 auto 28px; }
  .confirm-card { background:var(--card2); border:1px solid var(--border); border-radius:14px; padding:20px; max-width:360px; margin:0 auto 28px; text-align:left; }

  .powered-by { text-align:center; padding:20px; color:var(--muted); font-size:12px; border-top:1px solid var(--border); margin-top:40px; }
  .powered-by a { color:var(--blue); font-weight:500; }

  .sms-consent-box { display:flex; align-items:flex-start; gap:12px; padding:12px 14px; background:rgba(255,255,255,.03); border:1px solid var(--border); border-radius:10px; margin-bottom:14px; cursor:pointer; transition:border-color .2s; }
  .sms-consent-box:hover { border-color:var(--blue); }
  .sms-consent-box.checked { border-color:rgba(20,184,166,.5); background:rgba(20,184,166,.04); }
  .sms-consent-box input[type="checkbox"] { width:18px; height:18px; flex-shrink:0; margin-top:1px; accent-color:var(--teal); cursor:pointer; }
  .sms-consent-box label { font-size:12px; color:var(--muted); line-height:1.6; cursor:pointer; user-select:none; }

  .not-found { text-align:center; padding:80px 24px; }
  .not-found h2 { font-family:var(--head); font-size:40px; font-weight:800; margin-bottom:12px; }
  .not-found p  { color:var(--muted); font-size:16px; }
`;

function StepIndicator({ step }) {
  return (
    <div className="step-indicator">
      {[1,2,3,4].map((s,i) => (
        <span key={s} style={{ display:"flex", alignItems:"center" }}>
          <div className={`step-dot ${step > s ? "done" : step === s ? "active" : ""}`}>{step > s ? "✓" : s}</div>
          {i < 3 && <div className={`step-line ${step > s ? "done" : ""}`} />}
        </span>
      ))}
    </div>
  );
}

function Toast({ msg, type }) { return msg ? <div className={`toast ${type}`}>{msg}</div> : null; }

function DepositForm({ onSuccess, onBack, submitting }) {
  const stripe   = useStripe();
  const elements = useElements();
  const [paying, setPaying] = useState(false);
  const [err, setErr]       = useState(null);

  const handlePay = async e => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setPaying(true); setErr(null);
    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: "if_required",
    });
    if (error) { setErr(error.message); setPaying(false); }
    else if (paymentIntent?.status === "succeeded") { onSuccess(paymentIntent.id); }
    else { setErr("Payment did not complete. Please try again."); setPaying(false); }
  };

  return (
    <form onSubmit={handlePay}>
      <PaymentElement options={{ layout:"tabs" }} />
      {err && <div style={{ color:"var(--danger)", fontSize:13, marginTop:10, textAlign:"center" }}>{err}</div>}
      <div className="btn-row" style={{ marginTop:16 }}>
        <button type="button" className="btn-secondary" onClick={onBack}>← Back</button>
        <button type="submit" className="btn-primary" disabled={!stripe || paying || submitting}>
          {paying || submitting ? <><span className="spinner"/>Processing...</> : "Pay $50 Deposit →"}
        </button>
      </div>
    </form>
  );
}

export default function BookingPage() {
  const { slug } = useParams();
  const [detailer, setDetailer]   = useState(null);
  const [services, setServices]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [notFound, setNotFound]   = useState(false);

  const [step, setStep]           = useState(1);
  const [service, setService]     = useState(null);
  const [vehicle, setVehicle]     = useState(null);
  const [calYear, setCalYear]     = useState(today.getFullYear());
  const [calMonth, setCalMonth]   = useState(today.getMonth());
  const [date, setDate]           = useState(null);   // { day, month, year }
  const [time, setTime]           = useState(null);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [blockedDays, setBlockedDays] = useState([]); // fully blocked dates as strings
  const [form, setForm]           = useState({ name:"", email:"", phone:"", address:"", notes:"" });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast]         = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [paymentIntentId, setPaymentIntentId] = useState(null);
  const [outOfRange, setOutOfRange]     = useState(false);
  const [distanceFromBase, setDistanceFromBase] = useState(null);
  const [geocoding, setGeocoding]       = useState(false);
  const [smsConsent, setSmsConsent]     = useState(false);
  const [estimatePhotos, setEstimatePhotos] = useState([]);
  const [quoteFlowOpen, setQuoteFlowOpen] = useState(false);

  const showToast = (msg, type="success") => { setToast({ msg, type }); setTimeout(() => setToast(null), 3500); };

  // ── LOAD DETAILER ──────────────────────────────────
  useEffect(() => {
    const load = async () => {
      const { data: det } = await supabase
        .from("detailers").select("*").eq("slug", slug).single();
      if (!det) { setNotFound(true); setLoading(false); return; }
      setDetailer(det);

      const [{ data: svcs }, { data: blocked }] = await Promise.all([
        supabase.from("services").select("*, service_pricing(*)")
          .eq("detailer_id", det.id).eq("active", true).order("sort_order"),
        supabase.from("blocked_times").select("date").eq("detailer_id", det.id),
      ]);
      setServices(svcs ?? []);
      setBlockedDays((blocked ?? []).map(b => b.date));
      setLoading(false);
    };
    load();
  }, [slug]);

  // ── LOAD BUSY SLOTS ────────────────────────────────
  useEffect(() => {
    if (!date || !detailer) return;
    const load = async () => {
      const { data } = await supabase.rpc("get_busy_slots", {
        p_detailer_id: detailer.id,
        p_date: toIsoDate(date.year, date.month, date.day),
      });
      setBookedSlots(data?.map(r => r.slot) ?? []);
    };
    load();
  }, [date, detailer]);

  // ── CREATE PAYMENT INTENT (deposit) ───────────────
  const createPaymentIntent = async () => {
    const svc = services.find(s => s.id === service);
    const res = await fetch("/api/create-payment-intent", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        amount:            5000, // $50.00 in cents
        stripe_account_id: detailer.stripe_account_id,
        metadata: {
          customer:    form.name,
          service:     svc?.name,
          detailer_id: detailer.id,
          slug:        detailer.slug,
        },
      }),
    });
    const data = await res.json();
    if (data.clientSecret) {
      setClientSecret(data.clientSecret);
      setPaymentIntentId(data.clientSecret.split("_secret_")[0]);
    } else {
      showToast(data.error || "Payment setup failed. Please try again.", "error");
    }
  };

  // ── SUBMIT BOOKING ─────────────────────────────────
  const handleSubmit = async (depositPaid = false, piId = "") => {
    setSubmitting(true);
    const svc = services.find(s => s.id === service);
    const veh = VEHICLES.find(v => v.id === vehicle);
    const price = getPrice(svc, vehicle);

    // Geocode customer address for map view
    let customerLat = null, customerLng = null;
    if (form.address && GOOGLE_MAPS_KEY) {
      const coords = await geocodeAddress(form.address);
      if (coords) { customerLat = coords.lat; customerLng = coords.lng; }
    }

    const { data: insertedBookings, error } = await supabase.from("bookings").insert([{
      detailer_id:       detailer.id,
      name:              form.name,
      email:             form.email,
      phone:             form.phone,
      address:           form.address,
      notes:             form.notes,
      service_id:        svc.id,
      service_name:      svc.name,
      vehicle_type:      vehicle,
      vehicle_label:     veh.label,
      amount:            price,
      date:              date ? toIsoDate(date.year, date.month, date.day) : "",
      time:              time,
      status:            "pending",
      deposit_paid:      depositPaid,
      payment_intent_id: piId,
      customer_lat:      customerLat,
      customer_lng:      customerLng,
    }]).select("id");
    const newBookingId = insertedBookings?.[0]?.id;

    // Upload estimate photos if the customer attached any (Pro+ detailers only)
    if (!error && newBookingId && estimatePhotos.length > 0) {
      try {
        const encoded = await Promise.all(estimatePhotos.map(resizeAndEncode));
        await fetch("/api/upload-estimate-photos", {
          method:  "POST",
          headers: { "Content-Type": "application/json" },
          body:    JSON.stringify({ booking_id: newBookingId, photos: encoded }),
        });
      } catch { /* non-critical — booking already saved */ }
    }

    // Auto-create the customer record so they appear in the CRM right away.
    // job_count and total_spent stay at 0 here — both are incremented on
    // job completion (Dashboard updateStatus + complete-job.js) so cancellations
    // and declines never inflate the lifetime numbers.
    if (!error) {
      const { data: existing } = await supabase.from("customers")
        .select("id").eq("detailer_id", detailer.id).eq("email", form.email).maybeSingle();
      if (!existing) {
        await supabase.from("customers").insert([{
          detailer_id: detailer.id,
          name:        form.name,
          email:       form.email,
          phone:       form.phone,
          job_count:   0,
          total_spent: 0,
        }]);
      } else if (form.phone) {
        await supabase.from("customers").update({ phone: form.phone }).eq("id", existing.id);
      }
    }

    setSubmitting(false);
    if (error) { showToast("Something went wrong. Please try again.", "error"); }
    else {
      // Send booking confirmation email to customer
      if (newBookingId) {
        try {
          await fetch("/api/send-email", {
            method:  "POST",
            headers: { "Content-Type": "application/json" },
            body:    JSON.stringify({ type: "booking", booking_id: newBookingId }),
          });
        } catch(e) { console.warn("Email failed:", e); }
      }
      setSubmitted(true);
    }
  };

  const getPrice = (svc, vehId) => {
    if (!svc || !vehId) return 0;
    const p = svc.service_pricing?.find(p => p.vehicle_type === vehId);
    return p?.price ?? 0;
  };

  const reset = () => {
    setSubmitted(false); setStep(1); setService(null); setVehicle(null);
    setDate(null); setTime(null); setForm({ name:"", email:"", phone:"", address:"", notes:"" });
    setClientSecret(null); setPaymentIntentId(null); setSmsConsent(false);
  };

  const svcObj    = services.find(s => s.id === service);
  const vehObj    = VEHICLES.find(v => v.id === vehicle);
  const price     = getPrice(svcObj, vehicle);
  const needsDeposit = detailer?.require_deposit && detailer?.plan === "pro" && detailer?.stripe_connected;

  // Working hours derived from detailer settings
  const workDays  = (detailer?.work_days || "mon,tue,wed,thu,fri,sat").split(",");
  const timeSlots = getTimeSlots(detailer?.work_start ?? 8, detailer?.work_end ?? 17);
  const isNonWorkDay = (year, month, day) => {
    const key = DAY_KEYS[new Date(year, month, day).getDay()];
    return !workDays.includes(key);
  };
  const totalSteps   = 4; // steps don't change, deposit is inside step 4

  if (loading) return <div style={{ background:"#080b10", minHeight:"100vh" }} />;
  if (notFound) return (
    <>
      <style>{css}</style>
      <div className="not-found">
        <h2>PAGE NOT FOUND</h2>
        <p>This booking page doesn't exist.<br />Check the URL and try again.</p>
      </div>
    </>
  );

  const bizName   = detailer.business_name;
  const isPro     = detailer.plan === "pro" || detailer.plan === "pro_plus";
  const isProPlus = detailer.plan === "pro_plus";

  const resizeAndEncode = (file) => new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = e => {
      const img = new Image();
      img.onload = () => {
        const MAX    = 1024;
        const scale  = Math.min(1, MAX / Math.max(img.width, img.height));
        const canvas = document.createElement("canvas");
        canvas.width  = Math.round(img.width  * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve({ data: canvas.toDataURL("image/jpeg", 0.82).split(",")[1], type: "image/jpeg" });
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });

  return (
    <>
      <style>{css}</style>
      {quoteFlowOpen && (
        <QuoteFlow
          slug={detailer.slug}
          detailerName={detailer.business_name}
          onClose={() => setQuoteFlowOpen(false)}
          onBookService={({ serviceId, bodyType }) => {
            if (serviceId && services.some((s) => s.id === serviceId)) {
              setService(serviceId);
            }
            // Map AI-flow body type → booking-page vehicle id
            const map = { coupe: "sedan", sedan: "sedan", suv: "suv", pickup: "truck", minivan: "van" };
            if (bodyType && map[bodyType]) setVehicle(map[bodyType]);
            setStep(1);
            setQuoteFlowOpen(false);
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
        />
      )}
      <div className="bp-shell">
        <nav className="bp-nav">
          <div className="bp-logo">
            {bizName.includes(" ")
              ? <>{bizName.split(" ").slice(0,-1).join(" ")} <span>{bizName.split(" ").at(-1)}</span></>
              : <span>{bizName}</span>
            }
          </div>
        </nav>

        <div className="booking-page fade-in">
          {submitted ? (
            <div className="success-screen">
              <div className="success-icon">🎉</div>
              <h2>YOU'RE ALL <span style={{ color:"var(--teal)" }}>SET!</span></h2>
              <p>Your booking is submitted. You'll hear from {bizName} once it's confirmed.</p>
              <div className="confirm-card">
                {[["Service",svcObj?.name],["Vehicle",vehObj?.label],["Date",date ? formatDate(toIsoDate(date.year, date.month, date.day)) : ""],["Time",time],["Total",fmt$(price)]].map(([k,v]) => (
                  <div className="summary-row" key={k}>
                    <span className="summary-label">{k}</span><span>{v}</span>
                  </div>
                ))}
              </div>
              <button className="btn-primary" style={{ maxWidth:280, margin:"0 auto" }} onClick={reset}>Book Again</button>
              {!isPro && (
                <div className="powered-by">
                  Powered by <a href="/" target="_blank" rel="noreferrer">NEO Detail</a>
                </div>
              )}
            </div>
          ) : (
            <>
              <div className="booking-hero">
                <h1>BOOK YOUR <span>DETAIL</span></h1>
                <p>Fast online booking. No sign-up required.</p>
                <div className="biz-meta">
                  {detailer.phone && <span>📞 {detailer.phone}</span>}
                  {detailer.city  && <span>📍 {detailer.city}{detailer.state ? `, ${detailer.state}` : ""}</span>}
                </div>
                {isProPlus && (
                  <button
                    type="button"
                    onClick={() => setQuoteFlowOpen(true)}
                    style={{
                      marginTop: 18,
                      background: "linear-gradient(90deg, var(--blue), var(--teal))",
                      color: "#fff",
                      border: "none",
                      borderRadius: 12,
                      padding: "12px 22px",
                      fontWeight: 700,
                      fontSize: 14,
                      cursor: "pointer",
                      boxShadow: "0 0 20px var(--blue-glow)",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    ✨ Get Instant AI Quote
                  </button>
                )}
              </div>
              <StepIndicator step={step} />

              {/* STEP 1 — Service + Vehicle */}
              {step === 1 && (
                <div className="fade-in">
                  <div className="section-label">Choose Your Service</div>
                  <div className="services-grid">
                    {services.map(s => {
                      const p = getPrice(s, vehicle);
                      return (
                        <div key={s.id} className={`service-card ${service === s.id ? "selected" : ""}`} onClick={() => setService(s.id)}>
                          <div className="check">✓</div>
                          <div className="s-icon">{s.icon}</div>
                          <h3>{s.name}</h3>
                          <p>{s.description}</p>
                          <div className="price">{vehicle && p ? fmt$(p) : vehicle ? "—" : `From ${fmt$(s.service_pricing?.[0]?.price ?? 0)}`}</div>
                          {s.duration && <div className="dur">⏱ {s.duration}</div>}
                        </div>
                      );
                    })}
                  </div>
                  <div className="section-label">Vehicle Type</div>
                  <div className="vehicle-grid">
                    {VEHICLES.map(v => (
                      <div key={v.id} className={`vehicle-card ${vehicle === v.id ? "selected" : ""}`} onClick={() => setVehicle(v.id)}>
                        <div className="vicon">{v.icon}</div>
                        <span>{v.label}</span>
                      </div>
                    ))}
                  </div>
                  {price > 0 && (
                    <div className="summary-box" style={{ marginBottom:18 }}>
                      <div className="summary-row"><span className="summary-label">Selected</span><span>{svcObj?.name} — {vehObj?.label}</span></div>
                      <div className="summary-row"><span className="summary-label">Total</span><span>{fmt$(price)}</span></div>
                    </div>
                  )}
                  <button className="btn-primary full" disabled={!service || !vehicle} onClick={() => setStep(2)}>Continue →</button>
                </div>
              )}

              {/* STEP 2 — Date + Time */}
              {step === 2 && (() => {
                const { firstDay, daysInMonth } = getCalendarData(calYear, calMonth);
                const prevMonth = () => { if (calMonth === 0) { setCalMonth(11); setCalYear(y=>y-1); } else setCalMonth(m=>m-1); setDate(null); setTime(null); };
                const nextMonth = () => { if (calMonth === 11) { setCalMonth(0); setCalYear(y=>y+1); } else setCalMonth(m=>m+1); setDate(null); setTime(null); };
                const isPast     = d => new Date(calYear, calMonth, d) < today;
                const isBlocked  = d => blockedDays.includes(toIsoDate(calYear, calMonth, d));
                const isOffDay   = d => isNonWorkDay(calYear, calMonth, d);
                const isToday    = d => calYear === today.getFullYear() && calMonth === today.getMonth() && d === today.getDate();
                const isSelected = d => date && date.day === d && date.month === calMonth && date.year === calYear;
                return (
                <div className="fade-in">
                  <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12 }}>
                    <div className="section-label" style={{ margin:0 }}>Pick a Date</div>
                    <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                      <button className="btn-edit" onClick={prevMonth}>‹</button>
                      <span style={{ fontWeight:600, fontSize:14 }}>{MONTH_NAMES[calMonth]} {calYear}</span>
                      <button className="btn-edit" onClick={nextMonth}>›</button>
                    </div>
                  </div>
                  <div className="date-grid">
                    {WEEK_DAYS.map(d => <div className="day-label" key={d}>{d}</div>)}
                    {Array.from({length:firstDay}).map((_,i) => <div className="date-btn empty" key={`e${i}`} />)}
                    {Array.from({length:daysInMonth},(_,i)=>i+1).map(d => (
                      <button key={d}
                        className={`date-btn ${isSelected(d)?"selected":""} ${isToday(d)?"today":""} ${isBlocked(d)?"booked":""}`}
                        disabled={isPast(d) || isBlocked(d) || isOffDay(d)}
                        style={isPast(d)||isBlocked(d)||isOffDay(d)?{opacity:.25,cursor:"not-allowed"}:{}}
                        title={isBlocked(d) ? "Not available" : isOffDay(d) ? "Not a working day" : ""}
                        onClick={() => { if (!isPast(d) && !isBlocked(d) && !isOffDay(d)) { setDate({ day:d, month:calMonth, year:calYear }); setTime(null); } }}>
                        {d}
                      </button>
                    ))}
                  </div>
                  <div className="section-label" style={{ marginTop:24 }}>Pick a Time</div>
                  <div className="time-grid">
                    {timeSlots.map(t => (
                      <button key={t} className={`time-btn ${time===t?"selected":""} ${bookedSlots.includes(t)?"booked":""}`}
                        onClick={() => !bookedSlots.includes(t) && setTime(t)}>
                        {t}{bookedSlots.includes(t) ? " — Taken" : ""}
                      </button>
                    ))}
                  </div>
                  <div className="btn-row">
                    <button className="btn-secondary" onClick={() => setStep(1)}>← Back</button>
                    <button className="btn-primary" disabled={!date || !time} onClick={() => setStep(3)}>Continue →</button>
                  </div>
                </div>
                );
              })()}

              {/* STEP 3 — Contact Info */}
              {step === 3 && (
                <div className="fade-in">
                  <div className="section-label">Your Info</div>
                  <div className="form-grid">
                    {[
                      { key:"name",    label:"Full Name",       placeholder:"John Doe",        type:"text"  },
                      { key:"phone",   label:"Phone Number",    placeholder:"(555) 000-0000",  type:"tel"   },
                      { key:"email",   label:"Email Address",   placeholder:"you@email.com",   type:"email", full:true },
                      { key:"address", label:"Service Address", placeholder:"123 Main St",     type:"text",  full:true },
                      { key:"notes",   label:"Notes (optional)",placeholder:"Special requests...",type:"textarea", full:true },
                    ].map(({ key, label, placeholder, type, full }) => (
                      <div className={`form-group ${full ? "form-full" : ""}`} key={key}>
                        <label>{label}</label>
                        {type === "textarea"
                          ? <textarea placeholder={placeholder} value={form[key]} onChange={e => setForm(f=>({...f,[key]:e.target.value}))} />
                          : <input type={type} placeholder={placeholder} value={form[key]}
                              onChange={e => setForm(f=>({...f,[key]:e.target.value}))}
                              onBlur={async e => {
                                if (key === "address" && detailer?.service_lat && detailer?.service_lng && e.target.value && GOOGLE_MAPS_KEY) {
                                  setGeocoding(true);
                                  const coords = await geocodeAddress(e.target.value);
                                  setGeocoding(false);
                                  if (coords) {
                                    const dist = distanceMiles(detailer.service_lat, detailer.service_lng, coords.lat, coords.lng);
                                    setDistanceFromBase(Math.round(dist));
                                    setOutOfRange(dist > (detailer.service_radius || 25));
                                  }
                                }
                              }}
                            />
                        }
                      </div>
                    ))}
                  </div>
                  {geocoding && (
                    <div style={{ fontSize:12, color:"var(--muted)", margin:"8px 0 14px", display:"flex", alignItems:"center", gap:6 }}>
                      <span className="spinner" style={{ width:12, height:12, border:"2px solid rgba(59,130,246,.2)", borderTopColor:"var(--blue)" }}/>
                      Checking service area...
                    </div>
                  )}
                  {outOfRange && (
                    <div style={{ background:"rgba(245,158,11,.08)", border:"1px solid rgba(245,158,11,.3)", borderRadius:12, padding:14, marginBottom:14, display:"flex", gap:10, alignItems:"flex-start" }}>
                      <span style={{ fontSize:20 }}>⚠️</span>
                      <div>
                        <div style={{ fontWeight:600, fontSize:13, color:"var(--warn)" }}>Outside Service Area</div>
                        <div style={{ fontSize:12, color:"var(--muted)", marginTop:3 }}>
                          Your address is ~{distanceFromBase} miles away. {detailer.business_name} typically serves within {detailer.service_radius} miles. You can still submit, and they'll confirm if they can accommodate you.
                        </div>
                      </div>
                    </div>
                  )}
                  {/* Vehicle photos — Pro+ detailers only */}
                  {isProPlus && (
                    <div className="form-group form-full" style={{ marginBottom:16 }}>
                      <label>Vehicle Photos <span style={{ fontWeight:400, fontSize:12, color:"var(--muted)" }}>(optional — helps us give you an accurate estimate)</span></label>
                      <div style={{ display:"flex", flexWrap:"wrap", gap:8, marginTop:8 }}>
                        {estimatePhotos.map((f, i) => (
                          <div key={i} style={{ position:"relative", width:72, height:72, flexShrink:0 }}>
                            <img src={URL.createObjectURL(f)} alt="" style={{ width:72, height:72, objectFit:"cover", borderRadius:8, border:"1px solid var(--border)" }} />
                            <button type="button"
                              onClick={() => setEstimatePhotos(ps => ps.filter((_,j) => j !== i))}
                              style={{ position:"absolute", top:-6, right:-6, width:18, height:18, borderRadius:"50%", background:"#ef4444", color:"#fff", border:"none", fontSize:10, lineHeight:1, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>✕</button>
                          </div>
                        ))}
                        {estimatePhotos.length < 4 && (
                          <label style={{ width:72, height:72, border:"2px dashed var(--border)", borderRadius:8, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", cursor:"pointer", gap:3, flexShrink:0 }}>
                            <input type="file" accept="image/*" multiple style={{ display:"none" }}
                              onChange={e => {
                                const files = Array.from(e.target.files).slice(0, 4 - estimatePhotos.length);
                                setEstimatePhotos(ps => [...ps, ...files].slice(0, 4));
                                e.target.value = "";
                              }} />
                            <span style={{ fontSize:22 }}>📷</span>
                            <span style={{ fontSize:10, color:"var(--muted)" }}>Add</span>
                          </label>
                        )}
                      </div>
                    </div>
                  )}

                  <div className={`sms-consent-box ${smsConsent ? "checked" : ""}`} onClick={() => setSmsConsent(v => !v)}>
                    <input
                      type="checkbox"
                      id="sms-consent"
                      checked={smsConsent}
                      onChange={e => setSmsConsent(e.target.checked)}
                      onClick={e => e.stopPropagation()}
                    />
                    <label htmlFor="sms-consent">
                      I agree to receive appointment confirmations, reminders, and status updates via SMS from <strong>{detailer?.business_name || "this business"}</strong>. Message &amp; data rates may apply. Reply <strong>STOP</strong> to unsubscribe, <strong>HELP</strong> for help. Consent is not a condition of purchase.
                    </label>
                  </div>
                  <div className="btn-row">
                    <button className="btn-secondary" onClick={() => setStep(2)}>← Back</button>
                    <button className="btn-primary" disabled={!form.name || !form.email || !form.phone || !smsConsent} onClick={() => setStep(4)}>Review →</button>
                  </div>
                </div>
              )}

              {/* STEP 4 — Review */}
              {step === 4 && (
                <div className="fade-in">
                  <div className="section-label">Review & Confirm</div>
                  <div className="summary-box">
                    {[["Service",svcObj?.name],["Vehicle",vehObj?.label],["Date",date ? formatDate(toIsoDate(date.year, date.month, date.day)) : ""],["Time",time],["Name",form.name],["Email",form.email],["Phone",form.phone],["Address",form.address||"—"],["Total",fmt$(price)]].map(([k,v]) => (
                      <div className="summary-row" key={k}>
                        <span className="summary-label">{k}</span>
                        <span style={k==="Total"?{color:"var(--teal)"}:{}}>{v}</span>
                      </div>
                    ))}
                  </div>
                  {needsDeposit ? (
                    <>
                      <div style={{ background:"rgba(59,130,246,.06)", border:"1px solid rgba(59,130,246,.2)", borderRadius:12, padding:14, marginBottom:18, textAlign:"center" }}>
                        <div style={{ fontWeight:600, fontSize:14, marginBottom:4 }}>💳 $50 Deposit Required</div>
                        <div style={{ color:"var(--muted)", fontSize:13 }}>A $50 deposit is required to secure your booking. The remaining balance is collected at time of service.</div>
                      </div>
                      {!clientSecret ? (
                        <div className="btn-row">
                          <button className="btn-secondary" onClick={() => setStep(3)}>← Back</button>
                          <button className="btn-primary" onClick={createPaymentIntent} disabled={submitting}>
                            Continue to Payment →
                          </button>
                        </div>
                      ) : (
                        <Elements stripe={stripePromise} options={{ clientSecret, appearance:{ theme:"night" } }}>
                          <DepositForm
                            onSuccess={piId => handleSubmit(true, piId)}
                            onBack={() => { setClientSecret(null); setPaymentIntentId(null); }}
                            submitting={submitting}
                          />
                        </Elements>
                      )}
                    </>
                  ) : (
                    <>
                      <p style={{ color:"var(--muted)", fontSize:13, marginBottom:18, textAlign:"center" }}>Payment collected at time of service. Free cancellation 24hrs before.</p>
                      <div className="btn-row">
                        <button className="btn-secondary" onClick={() => setStep(3)}>← Back</button>
                        <button className="btn-primary" onClick={() => handleSubmit(false, "")} disabled={submitting}>
                          {submitting ? <><span className="spinner"/>Submitting...</> : "✓ Confirm Booking"}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}

              {!isPro && (
                <div className="powered-by">
                  Powered by <a href="/" target="_blank" rel="noreferrer">NEO Detail</a>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {toast && <Toast {...toast} />}
    </>
  );
}
